import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Select, TextArea, Modal, Table } from '../components/Common';

/**
 * The network inventory: a client's IP addresses and CIDR ranges.
 *
 * The counterpart to Applications for the other half of the testing. A network
 * or segmentation engagement draws its scope from here rather than from an
 * application, so a range is described once — what it is, what it is for,
 * where it lives, whether it is in PCI scope — and every engagement that
 * covers it inherits that description.
 *
 * One row is one scope entry: a single address or a single range, never a
 * list. That is what makes a row copyable straight into an engagement's scope
 * and exportable as one line of a target file.
 */

const YES_NO = [{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }];
const BLANK = {
  clientId: '', name: '', value: '', purpose: '',
  location: '', cloudEnvironment: '', pci: 'false', comments: '', tags: [],
};

async function errorText(res, fallback) {
  const body = await res.json().catch(() => null);
  return body?.error || `${fallback} (HTTP ${res.status})`;
}


/**
 * Free-text tags with suggestions.
 *
 * Typing is unconstrained — any label is allowed, which is what was asked for.
 * The suggestions are a native <datalist>, the same thing PlaybookTemplates
 * uses for its category field, so the browser does the matching and there is
 * no popup to get stuck behind a modal.
 *
 * Enter or comma commits; Backspace on an empty box removes the last chip.
 * The server lowercases and de-duplicates on write, so a typed "PCI" and a
 * picked "pci" are the same tag — the suggestions stay a short list of real
 * labels rather than every spelling anyone has used.
 */
function TagInput({ value, onChange, suggestions, listId }) {
  const [draft, setDraft] = useState('');
  const tags = value || [];

  const commit = (raw) => {
    const tag = String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!tag || tags.includes(tag)) { setDraft(''); return; }
    onChange([...tags, tag]);
    setDraft('');
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
        Tags
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
                    padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                    background: 'var(--bg-input)' }}>
        {tags.map(t => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px',
                                 borderRadius: 12, fontSize: 12, background: 'rgba(79,143,255,0.12)', color: '#4f8fff' }}>
            {t}
            <button type="button" onClick={() => onChange(tags.filter(x => x !== t))}
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer',
                       fontSize: 14, lineHeight: 1, padding: 0 }}>&times;</button>
          </span>
        ))}
        <input
          value={draft}
          list={listId}
          placeholder={tags.length ? '' : 'Type a tag and press Enter'}
          onChange={e => {
            // Only a datalist PICK commits. Matching on "the value equals a
            // suggestion" looked equivalent and was not: typing pci-dss passes
            // through "pci" on the way, so an existing "pci" tag committed
            // itself mid-word and left "-dss" behind in the box. Browsers
            // report a pick as insertReplacementText; typing never is. Enter,
            // comma and blur still commit, so nothing is lost where a browser
            // does not report it.
            const v = e.target.value;
            if (e.nativeEvent?.inputType === 'insertReplacementText'
                && suggestions.some(([t]) => t === v)) commit(v);
            else setDraft(v);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(draft); }
            else if (e.key === 'Backspace' && !draft && tags.length) onChange(tags.slice(0, -1));
          }}
          onBlur={() => commit(draft)}
          style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent',
                   color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', padding: '2px 0' }} />
        <datalist id={listId}>
          {suggestions.map(([t, n]) => <option key={t} value={t} label={`${t} (${n})`} />)}
        </datalist>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
        Any label you like. Saved lowercase, so PCI and pci are the same tag.
        {suggestions.length > 0 && ' Already in use: '}
        {suggestions.slice(0, 6).map(([t]) => (
          <button key={t} type="button" onClick={() => commit(t)}
            style={{ background: 'none', border: 'none', padding: '0 4px', cursor: 'pointer',
                     color: 'var(--primary)', fontSize: 11, fontFamily: 'inherit' }}>{t}</button>
        ))}
      </div>
    </div>
  );
}

export default function Networks() {
  const { apiFetch, user, hasGroup } = useAuth();
  const [networks, setNetworks] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState({ client: '', pciOnly: false, search: '', tag: '' });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  const isMsp = user?.isMsp || hasGroup('Super Admins');
  const canManage = hasGroup('Application Managers') || hasGroup('Super Admins');

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/networks');
      if (!res.ok) { setLoadError(await errorText(res, 'Could not load networks')); return; }
      setLoadError('');
      setNetworks(await res.json());
      if (isMsp) {
        const c = await apiFetch('/clients');
        if (c.ok) setClients(await c.json());
      }
    } catch (e) {
      setLoadError(String(e.message || e));
    }
  }, [apiFetch, isMsp]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...BLANK, clientId: isMsp ? (filter.client || '') : (user?.clientId || '') });
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row.id);
    setForm({
      clientId: row.client_id,
      name: row.name || '',
      value: row.value || '',
      purpose: row.purpose || '',
      location: row.location || '',
      cloudEnvironment: row.cloud_environment || '',
      pci: row.pci ? 'true' : 'false',
      comments: row.comments || '',
      tags: Array.isArray(row.tags) ? row.tags : [],
    });
    setFormError('');
    setShowModal(true);
  };

  const save = async () => {
    setFormError('');
    if (!form.name.trim()) { setFormError('A network or system name is required'); return; }
    if (!form.value.trim()) { setFormError('An IP address or CIDR range is required'); return; }
    if (!editing && !form.clientId) { setFormError('Choose a client'); return; }

    setSaving(true);
    const payload = {
      clientId: form.clientId,
      name: form.name.trim(),
      value: form.value.trim(),
      purpose: form.purpose || null,
      location: form.location || null,
      cloudEnvironment: form.cloudEnvironment || null,
      pci: form.pci === 'true',
      comments: form.comments || null,
      tags: form.tags || [],
    };
    const send = (body) => editing
      ? apiFetch(`/networks/${editing}`, { method: 'PUT', body: JSON.stringify(body) })
      : apiFetch('/networks', { method: 'POST', body: JSON.stringify(body) });

    try {
      let res = await send(payload);

      // The server refuses a repeated address unless told otherwise, and hands
      // back what is already there. The same range at two sites is legitimate
      // — overlapping RFC1918 space across data centres is ordinary — so this
      // asks rather than blocks, but it shows the existing entries so the
      // answer is informed: seeing "DMZ, London DC" is usually enough to
      // realise you are about to add it twice.
      if (res.status === 409) {
        const body = await res.json().catch(() => null);
        if (body?.code === 'DUPLICATE_VALUE') {
          const lines = (body.existing || []).map(e => {
            const detail = [e.purpose, e.location, e.cloud_environment]
              .filter(Boolean).join(' · ');
            return `  • ${e.name}${detail ? ` — ${detail}` : ''}${e.pci ? '  [PCI]' : ''}`;
          }).join('\n');
          const ok = window.confirm(
            `${body.value} is already listed for this client:\n\n${lines}\n\n` +
            `Add it again anyway?\n\n` +
            `Do this when it is genuinely a different network that happens to ` +
            `use the same address space — a range at two sites, say. Give it a ` +
            `location or tag that tells them apart.`
          );
          if (!ok) { setFormError(''); return; }
          res = await send({ ...payload, allowDuplicate: true });
        } else {
          setFormError(body?.error || 'Could not save network (HTTP 409)');
          return;
        }
      }

      // The modal stays open on failure. Closing it would discard what was
      // typed and look exactly like a save.
      if (!res.ok) { setFormError(await errorText(res, 'Could not save network')); return; }
      setShowModal(false);
      setForm(BLANK);
      await load();
    } catch (e) {
      setFormError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Remove ${row.value} (${row.name}) from the inventory?\n\n` +
      `Engagements already scoped to it keep that scope — scope items hold the ` +
      `address itself, not a reference to this entry.`
    )) return;
    const res = await apiFetch(`/networks/${row.id}`, { method: 'DELETE' });
    if (!res.ok) { window.alert(await errorText(res, 'Could not delete network')); return; }
    load();
  };

  const visible = useMemo(() => networks.filter(n => {
    if (filter.client && n.client_id !== filter.client) return false;
    if (filter.pciOnly && !n.pci) return false;
    if (filter.tag && !(n.tags || []).includes(filter.tag)) return false;
    if (filter.search) {
      const q = filter.search.toLowerCase();
      const hay = [n.name, n.value, n.purpose, n.location, n.cloud_environment, n.client_name, ...(n.tags || [])]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [networks, filter]);

  // Suggestions come from what this client already uses, commonest first, so
  // the established label is the first thing offered rather than whichever is
  // alphabetically first. The server lowercases on write, so these are already
  // the canonical forms and a typed "PCI" lands on the same tag as "pci".
  const tagSuggestions = useMemo(() => {
    const counts = new Map();
    for (const n of networks) {
      if (filter.client && n.client_id !== filter.client) continue;
      for (const t of (n.tags || [])) counts.set(t, (counts.get(t) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [networks, filter.client]);

  const stats = useMemo(() => ({
    total: visible.length,
    pci: visible.filter(n => n.pci).length,
    ranges: visible.filter(n => n.entry_type === 'CIDR Range').length,
    addresses: visible.filter(n => n.entry_type === 'IP Address').length,
  }), [visible]);

  // The inventory as a target file, same format as an engagement's scope
  // export: one entry per line, LF, no header and no byte-order mark. A BOM
  // would be read as part of the first target.
  const exportTxt = () => {
    const lines = [...new Set(visible.map(n => (n.value || '').trim()).filter(Boolean))];
    if (!lines.length) return;
    const blob = new Blob([lines.join('\n') + '\n'], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const label = filter.client
      ? (clients.find(c => c.id === filter.client)?.name || 'client')
      : 'all-clients';
    a.download = `networks-${label.replace(/[^a-zA-Z0-9._-]+/g, '-')}${filter.pciOnly ? '-pci' : ''}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const columns = [
    ...(isMsp ? [{ key: 'client_name', label: 'Client' }] : []),
    { key: 'name', label: 'Name', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'value', label: 'IP / CIDR', render: (v, row) => (
      <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12 }}>
        {v}{' '}
        <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>
          {row.entry_type === 'CIDR Range' ? 'range' : ''}
        </span>
      </span>
    )},
    { key: 'pci', label: 'PCI', render: v => v
      ? <Badge style={{ background: 'rgba(239,68,68,0.12)', color: '#dc2626' }}>PCI</Badge>
      : <span style={{ color: 'var(--text-muted)' }}>—</span> },
    { key: 'tags', label: 'Tags', render: (v) => (v && v.length) ? (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {v.map(t => (
          <button key={t} type="button" title={`Filter by ${t}`}
            onClick={e => { e.stopPropagation(); setFilter(f => ({ ...f, tag: f.tag === t ? '' : t })); }}
            style={{ padding: '1px 7px', borderRadius: 10, fontSize: 11, cursor: 'pointer',
                     border: '1px solid var(--border)', fontFamily: 'inherit',
                     background: 'rgba(79,143,255,0.10)', color: '#4f8fff' }}>{t}</button>
        ))}
      </div>
    ) : <span style={{ color: 'var(--text-muted)' }}>—</span> },
    { key: 'purpose', label: 'Purpose', render: v => v || '—' },
    { key: 'location', label: 'Location / Region', render: v => v || '—' },
    { key: 'cloud_environment', label: 'Cloud Environment', render: v => v || '—' },
    ...(canManage ? [{ key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }}
          onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
        <Button variant="danger" style={{ padding: '4px 10px', fontSize: 12 }}
          onClick={e => { e.stopPropagation(); remove(row); }}>Delete</Button>
      </div>
    )}] : []),
  ];

  const card = (label, value, color) => (
    <Card style={{ padding: 14, flex: 1, minWidth: 130 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
    </Card>
  );

  return (
    <div style={{ maxWidth: '100%', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.3px' }}>Networks</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            IP addresses and ranges a network or segmentation engagement draws its scope from
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={exportTxt} disabled={!visible.length}
            title="Download the listed entries, one per line, for nmap -iL and similar">
            Export .txt
          </Button>
          {canManage && <Button onClick={openNew}>+ New Network</Button>}
        </div>
      </div>

      {loadError && (
        <Card style={{ padding: 12, marginBottom: 12, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.06)' }}>
          <span style={{ fontSize: 13, color: '#dc2626' }}>{loadError}</span>
        </Card>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        {card('Entries', stats.total, 'var(--primary)')}
        {card('In PCI scope', stats.pci, '#dc2626')}
        {card('Ranges', stats.ranges, '#8b5cf6')}
        {card('Addresses', stats.addresses, '#06b6d4')}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {isMsp && clients.length > 0 && (
          <select value={filter.client} onChange={e => setFilter(f => ({ ...f, client: e.target.value }))}
            style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-card)', color: filter.client ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <input value={filter.search} placeholder="Search name, address, purpose…"
          onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
          style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'inherit', minWidth: 240 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, background: filter.pciOnly ? 'rgba(239,68,68,0.08)' : 'var(--bg-card)' }}>
          <input type="checkbox" checked={filter.pciOnly}
            onChange={e => setFilter(f => ({ ...f, pciOnly: e.target.checked }))} />
          PCI only
        </label>
        {tagSuggestions.length > 0 && (
          <select value={filter.tag} onChange={e => setFilter(f => ({ ...f, tag: e.target.value }))}
            style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-card)', color: filter.tag ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">All tags</option>
            {tagSuggestions.map(([t, n]) => <option key={t} value={t}>{t} ({n})</option>)}
          </select>
        )}
        {visible.length !== networks.length && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Showing {visible.length} of {networks.length} — the totals above reflect this filter
          </span>
        )}
      </div>

      <Table columns={columns} data={visible} />

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editing ? 'Edit Network' : 'New Network'} width={560}>
        {formError && (
          <div style={{ padding: '8px 10px', marginBottom: 12, borderRadius: 6, fontSize: 12,
                        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#dc2626' }}>
            {formError}
          </div>
        )}
        {isMsp && !editing && (
          <Select label="Client" value={form.clientId}
            options={[{ value: '', label: 'Select client…' }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
            onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))} />
        )}
        <Input label="Network or System Name" value={form.name} placeholder="e.g. Card Data Environment"
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <Input label="IP Address or CIDR Range" value={form.value} placeholder="10.20.30.0/24 or 10.20.30.5"
          onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -6, marginBottom: 10 }}>
          One address or one range per entry. Whether it is an address or a range is
          worked out from what you type.
        </div>
        <Input label="Purpose" value={form.purpose} placeholder="What this network is for"
          onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} />
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Input label="Location / Region" value={form.location} placeholder="e.g. US-East, London DC"
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
          </div>
          <div style={{ flex: 1 }}>
            <Input label="Cloud Environment" value={form.cloudEnvironment} placeholder="e.g. AWS us-east-2, on-prem"
              onChange={e => setForm(f => ({ ...f, cloudEnvironment: e.target.value }))} />
          </div>
        </div>
        <Select label="In PCI Scope" value={form.pci} options={YES_NO}
          onChange={e => setForm(f => ({ ...f, pci: e.target.value }))} />
        <TagInput value={form.tags} suggestions={tagSuggestions} listId="network-tag-suggestions"
          onChange={tags => setForm(f => ({ ...f, tags }))} />
        <TextArea label="Comments" value={form.comments} rows={3}
          onChange={e => setForm(f => ({ ...f, comments: e.target.value }))} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (editing ? 'Update Network' : 'Create Network')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
